import type { ChartElementValue, ChartItem } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldArray, FieldInput, FieldSegmented, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { Typography } from 'components/uiKit/Typography'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { chainName } from 'utils/form'

import ChartFormValueItem from './ChartFormValueItem'
import type { ChartValueFormType } from './chartTypes'

const useShowNotPie = ({ name }: IFieldConfig<Block>) => {
  const values = useFormData<ChartElementValue>(name, 1)
  const hidden = values?.type === 'pie'
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowDescription = ({ name }: IFieldConfig<Block>) => {
  const values = useFormData<ChartElementValue>(name, 1)
  return useMemo(() => ({ hidden: !values.description }), [values.description])
}

const TYPE = genElementValueField({
  as: FieldSegmented<'line' | 'bar' | 'pie'>,
  name: 'type',
  props: {
    fluid: true,
    options: [
      { label: t('input.option.lineChart'), value: 'line' },
      { label: t('input.option.barChart'), value: 'bar' },
      { label: t('input.option.pieChart'), value: 'pie' },
    ],
  },
})

const DESCRIPTION = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'description',
  label: t('input.label.description'),
  useConfigs: [useShowNotPie],
})

const X = genElementValueField({
  as: FieldInput,
  name: 'x',
  label: t('elements.chart.form.x'),
  rules: [{ max: 30 }],
  props: { placeholder: t('input.placeholder.xAxis') },
  useConfigs: [useShowNotPie, useShowDescription],
})

const Y = genElementValueField({
  as: FieldInput,
  name: 'y',
  label: t('elements.chart.form.y'),
  rules: [{ max: 30 }],
  props: { placeholder: t('input.placeholder.yAxis') },
  useConfigs: [useShowNotPie, useShowDescription],
})

const ITEMS = genElementValueField({
  as: FieldArray<ChartItem>,
  name: 'items',
  props: {
    RenderItem: ChartFormValueItem,
    defaultItem: { label: 'Item', val: 0 },
    max: 12,
    min: 2,
  },
})

const ChartForm: ChartValueFormType = ({ name }) => {
  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, TYPE)} />
      <FormField config={chainName(name, DESCRIPTION)} />
      <FormField config={chainName(name, X)} />
      <FormField config={chainName(name, Y)} />
      <Typography styleType='hint'>{t('elements.chart.form.axisHint')}</Typography>
      <FormField config={chainName(name, ITEMS)} />
    </Padding>
  )
}

export default ChartForm
